/**
 * Phase A — Canonical derived-time record for a ticket.
 *
 * Single source of truth for the time/workload values that previously
 * rendered four independent, mutually-inconsistent ways on the
 * Dayboard:
 *
 *   - Card "Ticket duration" + tech-column workload header (raw `predicted_minutes`)
 *   - Card "Tech OptiTime" (`calculateWallMinutes(predicted, eff, buf)`)
 *   - Card live timer chip (`useSharedTimer` + session.started_at)
 *   - Card bottom "0h 0m" (`getTicketTotalActualMinutes` — closed sessions only)
 *   - Card OptiFinish / AutoBump / Run Long / Break section
 *
 * Output: one `DerivedTicketTime` record consumed by `TicketCard`,
 * `TicketTimerDisplay`, and `TechnicianColumn`. The same record drives
 * every surface, so they cannot disagree about elapsed, hold, run-long,
 * workload, or finish time again.
 *
 * Doctrinal contract (the six bucket rules this helper enforces):
 *
 *   - Bucket 1 §2.10: OptiFinish = Start + TechOptiTime + Bay Occupancy Extensions.
 *     "Extensions" never includes non-bay hold minutes.
 *   - Bucket 2 §2.6.2: Pause AutoBump extends OptiFinish ONLY when the
 *     pause is bay-blocking. The split is delegated to
 *     `getRolloverBayOccupancyImpact` (Phase 8.3 doctrine helper).
 *   - Bucket 2 §2.6.5 / Bucket 5 §5.6.3 (Q3 decision): pause_reason ===
 *     'break_failsafe' suppresses bay-blocking inflation by default.
 *     Opt-in via `shopConfig.breakFailsafeAutoPauseEnabled`.
 *   - Bucket 3 §3.12.1: when OptiFinish crosses
 *     `shift_end + willingness_roll_past_eod`, `requiresRolloverGate`
 *     is set so the UI can surface a governed rollover indicator
 *     instead of silently extending past EOD.
 *   - Bucket 4.5 §4.5.8: non-bay holds may still grow Due Out risk
 *     (`nonBayHoldMinutes`) but do NOT inflate bay OptiFinish.
 *   - Bucket 5 (Q1 decision): workload header reflects live projected
 *     bay/tech-blocking occupancy — `predicted_minutes` for unstarted,
 *     live projected for active, bay-blocking-only for paused, 0 for
 *     non-bay holds and terminal.
 *
 * Purity contract:
 *   - No DB I/O, no `Date.now()`, no React hooks.
 *   - Caller passes the wall clock (`nowInstant`) and the live elapsed
 *     seconds already derived from session state.
 *   - Negative outputs clamped to 0.
 *   - Does not mutate inputs.
 *
 * Composition (existing canonical helpers — do not re-implement):
 *
 *   - `calculateWallMinutes`, `calculateTicketOptiFinish`,
 *     `parseTimeToMinutes` (canonical time math)
 *   - `getRolloverBayOccupancyImpact` (Phase 8.3 bay-vs-non-bay classifier)
 *   - `getProductionState` (Phase 1.1 canonical production state)
 *
 * Phase A scope: HELPER + TESTS ONLY. No display-layer rewires here;
 * those are Phase B–E.
 */

import { resolvePlanningBufferPct } from '@/utils/planningBuffer';
import { runLongBaselineMinutes } from '../../supabase/functions/_shared/finishIntentDerivation';
import {
  calculateWallMinutes,
} from './canonicalTimeMath';
import {
  getRolloverBayOccupancyImpact,
  type NonBayHoldRolloverShape,
} from './nonBayHoldRolloverGuard';
import {
  getProductionState,
  type ProductionState,
  type ProductionStateTicketShape,
} from './ticketProductionState';

// =============================================================================
// Public types
// =============================================================================

/** Pause-time classification for a ticket. */
export type HoldChannel =
  /** No pause time accumulated yet. */
  | 'none'
  /** Pause time extends bay OptiFinish (Bucket 2 §2.6.2). */
  | 'bay_blocking'
  /** Pause time does NOT extend bay OptiFinish (Bucket 4.5 §4.5.8 / Bucket 3 §3.10.2). */
  | 'non_bay'
  /** Pause time suppressed for `break_failsafe` (Bucket 2 §2.6.5 + Bucket 5 §5.6.3, default). */
  | 'break_failsafe_suppressed'
  /** Terminal (deleted/cancelled/picked_up/completed) — no inflation. */
  | 'terminal_or_unknown';

/** Permissive break-window shape; minutes-from-midnight HH:MM strings. */
export interface BreakWindowInput {
  start?: string | null; // "12:00"
  end?: string | null;   // "12:30"
  date?: string | null;  // "2026-05-21" — present for non-recurring; absent on recurring
}

export interface DerivedTimeTicketShape
  extends ProductionStateTicketShape,
    NonBayHoldRolloverShape {
  predicted_minutes?: number | null;
  predictedMinutes?: number | null;
  run_long_minutes?: number | null;
  run_long_opti_baseline?: number | null;
  actual_start_time?: string | null;
  start_time?: string | null;
  /** The SERVER-derived finish (CORE-AC, ticket-finish-intent / run-long-autobump). See "Run-long finish" below. */
  opti_finish?: string | null;
  /**
   * NOV 3: the server's accounted eligible pause (the pause watermark S: in-bay, working hours only, scheduled breaks
   * excluded — __eligible_pause_between). Read once the ticket's own day has closed (see computeRawPauseMinutes).
   */
  accumulated_bump_minutes?: number | null;
  timer_status?: string | null;
}

export interface DerivedTimeTechnicianShape {
  efficiencyPercentage?: number | null;
  bufferPercentage?: number | null;
  recurring_breaks?: BreakWindowInput[] | null;
  non_recurring_breaks?: BreakWindowInput[] | null;
  shift_start?: string | null;
  shift_end?: string | null;
}

export interface DerivedTimeJobShape {
  standardMinutes?: number | null;
  separateTime?: boolean | null;
  separate_time?: boolean | null;
  diagnosticCompletedAt?: string | null;
  diagnostic_completed_at?: string | null;
}

export interface DerivedTimeShopConfig {
  /** Tech-shift EOD allowance in minutes (e.g. 30 = roll up to 30 min past shift end). */
  willingnessRollPastEodMin?: number | null;
  /**
   * Q3 opt-in: when true, `pause_reason === 'break_failsafe'` IS allowed
   * to inflate bay-blocking minutes. Default (false) means Bucket 5
   * wins — break_failsafe pause time is suppressed.
   */
  breakFailsafeAutoPauseEnabled?: boolean;
  /**
   * EOD-risk band in minutes BEFORE the hard gate. When OptiFinish lands
   * inside `[shiftEnd + willingness − band, shiftEnd + willingness]`,
   * the helper marks `isAtEodRisk = true` but not `requiresRolloverGate`.
   * Default 30 minutes.
   */
  eodRiskBandMin?: number | null;
}

export interface DerivedTicketTimeInput {
  ticket: DerivedTimeTicketShape;
  technician?: DerivedTimeTechnicianShape | null;
  jobs?: DerivedTimeJobShape[] | null;
  shopConfig?: DerivedTimeShopConfig | null;
  /**
   * The same start instant the existing OptiFinish calc uses (caller
   * may pass `displayStartTime` override for unstarted past-time
   * tickets). Pass `null` if start cannot be parsed.
   */
  startInstant: Date | null;
  /**
   * FLOYD-2 — THIS TECHNICIAN'S CLOSE FOR THIS TICKET'S SHOP-LOCAL DAY, as an
   * absolute instant.
   *
   * The EOD gate used to be built from `technician.shift_end`, and that was
   * wrong twice over:
   *
   *   1. `shift_end` is `deriveLegacyShiftEnvelope`'s `typical_week` MODE —
   *      the most frequent Mon–Fri close. Joe D's four 16:00 weekdays outvote
   *      his one 22:00 Monday, so a Monday was bounded at four o'clock.
   *   2. Parsed, it is minutes from SHOP-LOCAL midnight, and it was compared
   *      against minutes from UTC midnight. In UTC that is exact; at Floyd's
   *      it is 300 minutes of error in the direction that makes work look
   *      later than it is.
   *
   * An instant has no origin to get wrong. Callers build it the way
   * `useAutoBump` already does, which keeps ONE rollover calculation:
   *
   *     getTechShiftBoundaryUtc(
   *       shopDayLabel,
   *       getTechDayHours(tech.hours, shopDayLabel)?.close ?? tech.shift_end,
   *       shopTimezone,
   *     )
   *
   * `null` means the caller could not resolve a close. The gate then does NOT
   * fire — an unknown boundary is not a refusal, and asserting one from a
   * value known to be ambiguous is exactly what produced the false
   * "Cannot finish today".
   */
  shiftEndUtc?: Date | null;
  /**
   * Wall-clock "now" (already in canonical-clock terms). Used to
   * compute pause time (`wallClock − worked`) and EOD position.
   */
  nowInstant: Date;
  /**
   * Live elapsed seconds derived by the caller from its session state
   * (closed sessions + active-session live delta). Single source of
   * truth for both the chip and the bottom display.
   */
  liveElapsedSeconds: number;
  /**
   * PR #13 R-2a (W5 phantom pause). `false` while the caller has not yet loaded the live elapsed value (a card's
   * first render, a refetch, a missing session): pause is then UNKNOWN and none is derived, instead of reading the
   * whole wall time as pause. Omitted → known (other callers pass a real value).
   */
  liveElapsedKnown?: boolean;
  /**
   * The instant `liveElapsedSeconds` was sampled at (a running timer's live tick). The wall side of the pause
   * subtraction is read at this SAME instant, so two clocks sampled at different moments never invent pause.
   * Omitted / null → `nowInstant` (a frozen elapsed value: paused, stopped, accumulated-only).
   */
  liveElapsedSampledAtMs?: number | null;
  /**
   * Resolved break windows for THIS ticket's scheduled day in UTC
   * instants. Caller does the timezone conversion (the helper stays
   * timezone-agnostic to keep purity simple). Each entry's
   * `startUtcMs` / `endUtcMs` is the absolute UTC ms of the break
   * window.
   */
  breakWindowsUtc?: Array<{ startUtcMs: number; endUtcMs: number }> | null;
}

export interface DerivedTicketTimeLabels {
  /**
   * Label to use on the orange pause-chip on the card.
   *   - 'Pause AutoBump'  → bay-blocking pause time inflates OptiFinish
   *   - 'Hold time'       → non-bay hold time; does NOT inflate OptiFinish
   *   - ''                → no chip
   */
  holdChipLabel: string;
  /** Always "Run Long" today; centralised here so future changes don't fork. */
  runLongChipLabel: string;
}

export interface DerivedTicketTime {
  // Planned / book
  bookMinutes: number;
  predictedMinutes: number;
  techOptiTime: number;

  // Elapsed
  elapsedMinutes: number;
  liveElapsedMinutes: number;

  // Overruns / extensions
  runLongMinutes: number;
  bayBlockingPauseMinutes: number;
  nonBayHoldMinutes: number;
  breakMinutes: number;
  pausedDuringBreakMinutes: number;

  // Workload / occupancy
  scheduledOccupancyMinutes: number;
  workloadMinutes: number;

  // Finish & EOD
  optiFinishUtc: Date | null;
  optiFinishMinutesFromStart: number;
  isAtEodRisk: boolean;
  requiresRolloverGate: boolean;

  // Classification
  holdChannel: HoldChannel;
  productionState: ProductionState;

  // UI labels
  displayLabels: DerivedTicketTimeLabels;
}

// =============================================================================
// Internal helpers
// =============================================================================

function clampNonNeg(n: number): number {
  return Number.isFinite(n) && n > 0 ? n : 0;
}

function parseHmToMinutes(s: string | null | undefined): number | null {
  if (!s) return null;
  const m = String(s).slice(0, 5).split(':');
  const h = Number(m[0]);
  const mm = Number(m[1] || 0);
  if (!Number.isFinite(h) || !Number.isFinite(mm)) return null;
  return h * 60 + mm;
}

function sumBookFromJobs(jobs: DerivedTimeJobShape[] | null | undefined): number {
  if (!jobs || jobs.length === 0) return 0;
  let total = 0;
  for (const j of jobs) {
    if (j.separateTime || j.separate_time) continue;
    if (j.diagnosticCompletedAt || j.diagnostic_completed_at) continue;
    const sm = Number(j.standardMinutes ?? 0);
    if (Number.isFinite(sm) && sm > 0) total += sm;
  }
  return total;
}

function resolveBookMinutes(
  ticket: DerivedTimeTicketShape,
  jobs: DerivedTimeJobShape[] | null | undefined,
): number {
  const pmRaw = ticket.predicted_minutes ?? ticket.predictedMinutes;
  const pm = typeof pmRaw === 'number' ? pmRaw : Number(pmRaw);
  if (Number.isFinite(pm) && pm > 0) return pm;
  return sumBookFromJobs(jobs);
}

function isTerminalState(state: ProductionState): boolean {
  return (
    state === 'deleted' ||
    state === 'archived' ||
    state === 'picked_up' ||
    state === 'completed_awaiting_pickup'
  );
}

/**
 * Wall-clock time since `actual_start_time` that is not measured work: (wall − elapsed), in whole minutes.
 *
 * PR #13 R-2a (W5 phantom pause): both sides are taken at ONE instant and subtracted in SECONDS before rounding.
 * Previously the wall side used `nowInstant` (the card's minute clock) while the elapsed side came from another
 * sample, each floored to minutes separately — and a card whose elapsed value had not loaded yet passed 0, so a
 * running, never-paused ticket read its whole wall time as "Pause AutoBump". An unknown elapsed value now derives
 * no pause at all (never invented); a real pause (frozen elapsed, growing wall) still counts in full.
 */
function computeRawPauseMinutes(
  ticket: DerivedTimeTicketShape,
  nowInstant: Date,
  elapsedSeconds: number,
  elapsedKnown: boolean,
  elapsedSampledAtMs: number | null,
  dayBoundaryMs: number | null = null,
): number {
  if (!ticket.actually_started) return 0;
  if (!elapsedKnown) return 0;
  const ast = ticket.actual_start_time;
  if (!ast) return 0;
  const start = new Date(ast).getTime();
  if (!Number.isFinite(start)) return 0;
  const at = elapsedSampledAtMs != null && Number.isFinite(elapsedSampledAtMs) ? elapsedSampledAtMs : nowInstant.getTime();
  const wallSeconds = Math.floor((at - start) / 1000);
  const raw = clampNonNeg(Math.floor((wallSeconds - elapsedSeconds) / 60));
  // NOV 3 (Decision 2): wall − worked kept growing all night (Adam F's card: Pause AutoBump +908 for a 339-minute
  // eligible pause). Once the ticket's own day has closed, the card's wall clock stops at that close; minutes after
  // it count only as far as the server's eligible measure accounted them (accumulated_bump_minutes: working hours
  // of later days, in the bay, scheduled breaks excluded). Never more than the raw figure.
  if (dayBoundaryMs !== null && Number.isFinite(dayBoundaryMs) && at > dayBoundaryMs && dayBoundaryMs > start) {
    const atClose = clampNonNeg(Math.floor((Math.floor((dayBoundaryMs - start) / 1000) - elapsedSeconds) / 60));
    const accounted = clampNonNeg(Number(ticket.accumulated_bump_minutes ?? 0));
    return Math.min(raw, Math.max(atClose, accounted));
  }
  return raw;
}

/**
 * Compute the overlap between the active work window and the supplied
 * break windows (absolute UTC ms). Mirrors the TicketCard break-overlap
 * scan but uses pre-resolved UTC instants so this helper stays
 * timezone-agnostic.
 */
function computeBreakOverlapMinutes(
  workStartMs: number,
  workEndMs: number,
  breakWindowsUtc: Array<{ startUtcMs: number; endUtcMs: number }> | null | undefined,
): number {
  if (!breakWindowsUtc || breakWindowsUtc.length === 0) return 0;
  let total = 0;
  for (const brk of breakWindowsUtc) {
    if (workStartMs < brk.endUtcMs && workEndMs > brk.startUtcMs) {
      // Add full break duration (matches existing TicketCard behavior:
      // breaks are atomic — if any overlap exists, the whole break
      // counts toward extension).
      total += Math.max(0, Math.floor((brk.endUtcMs - brk.startUtcMs) / 60000));
    }
  }
  return total;
}

function computePausedDuringBreakMinutes(
  workStartMs: number,
  workEndMs: number,
  breakWindowsUtc: Array<{ startUtcMs: number; endUtcMs: number }> | null | undefined,
): number {
  if (!breakWindowsUtc || breakWindowsUtc.length === 0) return 0;
  let total = 0;
  for (const brk of breakWindowsUtc) {
    const overlapStart = Math.max(workStartMs, brk.startUtcMs);
    const overlapEnd = Math.min(workEndMs, brk.endUtcMs);
    if (overlapStart < overlapEnd) {
      total += Math.max(0, Math.floor((overlapEnd - overlapStart) / 60000));
    }
  }
  return total;
}

/**
 * Hold-channel classifier.
 *
 * Resolution order:
 *   1. No pause time → `none`.
 *   2. Terminal production state → `terminal_or_unknown`.
 *   3. `pause_reason === 'break_failsafe'` and shop has NOT opted in
 *      → `break_failsafe_suppressed` (Q3 default per Bucket 5).
 *   4. Phase 8.3 verdict — if bay-blocking minutes are non-zero →
 *      `bay_blocking`. Otherwise → `non_bay`.
 */
function classifyHoldChannel(
  ticket: DerivedTimeTicketShape,
  productionState: ProductionState,
  pauseMinutes: number,
  breakFailsafeAutoPauseEnabled: boolean,
): { channel: HoldChannel; bayBlockingMinutes: number; nonBayMinutes: number } {
  if (pauseMinutes <= 0) {
    return { channel: 'none', bayBlockingMinutes: 0, nonBayMinutes: 0 };
  }

  if (isTerminalState(productionState)) {
    return { channel: 'terminal_or_unknown', bayBlockingMinutes: 0, nonBayMinutes: 0 };
  }

  const isBreakFailsafe = (ticket.pause_reason ?? '').toString() === 'break_failsafe';
  if (isBreakFailsafe && !breakFailsafeAutoPauseEnabled) {
    return {
      channel: 'break_failsafe_suppressed',
      bayBlockingMinutes: 0,
      nonBayMinutes: 0,
    };
  }

  const verdict = getRolloverBayOccupancyImpact(ticket, {
    requestedHoldMinutes: pauseMinutes,
  });

  if (verdict.bayBlockingMinutes > 0) {
    return {
      channel: 'bay_blocking',
      bayBlockingMinutes: verdict.bayBlockingMinutes,
      nonBayMinutes: 0,
    };
  }
  return {
    channel: 'non_bay',
    bayBlockingMinutes: 0,
    nonBayMinutes: pauseMinutes,
  };
}

// =============================================================================
// Main entry point
// =============================================================================

export function getDerivedTicketTime(
  input: DerivedTicketTimeInput,
): DerivedTicketTime {
  const ticket = input.ticket;
  const technician = input.technician ?? null;
  const jobs = input.jobs ?? null;
  const shopConfig = input.shopConfig ?? null;
  const nowInstant = input.nowInstant;
  const liveElapsedSeconds = clampNonNeg(input.liveElapsedSeconds);
  const liveElapsedMinutes = Math.floor(liveElapsedSeconds / 60);

  // --- Planned / book / tech ---
  const bookMinutes = clampNonNeg(resolveBookMinutes(ticket, jobs));
  const efficiencyPct = Number(technician?.efficiencyPercentage ?? 100);
  const bufferPct = resolvePlanningBufferPct(technician?.bufferPercentage);
  const techOptiTime = bookMinutes > 0
    ? calculateWallMinutes(bookMinutes, efficiencyPct, bufferPct)
    : 0;

  // --- Production state classifier ---
  const productionState = getProductionState(ticket);

  // --- Run-long (only meaningful once started) ---
  // oct20d CORE-BF: the one shared derivation (the server measures run-long against the same frozen baseline).
  const effectiveOptiTime = runLongBaselineMinutes(ticket.run_long_opti_baseline, techOptiTime) ?? 0;
  const runLongLive = Math.max(0, liveElapsedMinutes - effectiveOptiTime);
  const runLongDb = clampNonNeg(Number(ticket.run_long_minutes ?? 0));
  const runLongMinutes = Math.max(runLongDb, runLongLive);
  // NOV 3 (Decision 3, run-long counted once): the run-long figure is measured against the FROZEN expectation
  // (CORE-BF), but a started day whose forecast share is larger than its frozen expectation (work moved onto it, or a
  // legacy growth share) already holds that difference inside techOptiTime. Occupancy (OptiFinish, workload) adds
  // only the overrun beyond the forecast; the Run Long display keeps the CORE-BF figure.
  const occupancyRunLongMinutes = clampNonNeg(runLongMinutes - clampNonNeg(techOptiTime - effectiveOptiTime));

  // --- Pause / hold time ---
  const dayBoundary = input.shiftEndUtc ?? null;
  const rawPauseMinutes = computeRawPauseMinutes(
    ticket,
    nowInstant,
    liveElapsedSeconds,
    input.liveElapsedKnown !== false,
    input.liveElapsedSampledAtMs ?? null,
  );
  // NOV 3 (Decision 2): the pause the card SHOWS and the workload CARRIES stops at the row's own close (see
  // computeRawPauseMinutes). The finish projection keeps the uncapped figure — for a row still paused after its close
  // that is "now + the work that remains", so the end-of-day gate still sees work that cannot finish today.
  const pauseMinutes = computeRawPauseMinutes(
    ticket,
    nowInstant,
    liveElapsedSeconds,
    input.liveElapsedKnown !== false,
    input.liveElapsedSampledAtMs ?? null,
    dayBoundary && Number.isFinite(dayBoundary.getTime()) ? dayBoundary.getTime() : null,
  );
  const breakFailsafeAutoPauseEnabled = shopConfig?.breakFailsafeAutoPauseEnabled === true;
  const holdForFinish = classifyHoldChannel(
    ticket,
    productionState,
    rawPauseMinutes,
    breakFailsafeAutoPauseEnabled,
  );
  const hold = pauseMinutes === rawPauseMinutes
    ? holdForFinish
    : classifyHoldChannel(ticket, productionState, pauseMinutes, breakFailsafeAutoPauseEnabled);

  // --- Break overlap ---
  let breakMinutes = 0;
  let pausedDuringBreakMinutes = 0;
  if (input.startInstant && Number.isFinite(input.startInstant.getTime())) {
    const startMs = input.startInstant.getTime();
    const actuallyStarted = ticket.actually_started === true;
    const actualStartMs = (() => {
      const ast = ticket.actual_start_time;
      if (!ast) return null;
      const t = new Date(ast).getTime();
      return Number.isFinite(t) ? t : null;
    })();

    // Work window: real (actualStart → now) when started; theoretical
    // (start → start + techOptiTime) when not. Matches the existing
    // TicketCard scan at lines 922-935.
    const workStart =
      actuallyStarted && actualStartMs !== null ? actualStartMs : startMs;
    const workEnd =
      actuallyStarted && actualStartMs !== null
        ? nowInstant.getTime()
        : startMs + techOptiTime * 60000;

    breakMinutes = computeBreakOverlapMinutes(workStart, workEnd, input.breakWindowsUtc);
    pausedDuringBreakMinutes = computePausedDuringBreakMinutes(
      workStart,
      workEnd,
      input.breakWindowsUtc,
    );
  }

  // --- OptiFinish ---
  // Per Bucket 1 §2.10: OptiFinish = Start + TechOptiTime + Extensions.
  // Extensions are bay-blocking only (Bucket 4.5 §4.5.8).
  // pausedDuringBreakMinutes is subtracted to prevent double-count with
  // the Break Line (Bucket 5 §5.6.5).
  const extensions =
    holdForFinish.bayBlockingMinutes +
    occupancyRunLongMinutes +
    breakMinutes -
    pausedDuringBreakMinutes;
  const optiFinishMinutesFromStart = techOptiTime + Math.max(0, extensions);

  let optiFinishUtc: Date | null = null;
  if (input.startInstant && Number.isFinite(input.startInstant.getTime())) {
    optiFinishUtc = new Date(
      input.startInstant.getTime() + optiFinishMinutesFromStart * 60_000,
    );
  }
  // Run-long finish (R3-A Option B, founder decision 2026-10-21: "Reopen Dayboard and verify it immediately displays
  // reconciled scheduling truth"). A RUNNING job that has run long has no remaining planned work, so the sum above is
  // ≈ now. The authority (CORE-AC, finishIntentDerivation.runLongFinish: "expected to finish five minutes from now")
  // wrote its finish, and the lane walk placed the next job one minute after THAT. Showing the sum instead left a
  // visible hole on the board (card 12:15, next job 12:21) — a second answer to the same question. The card shows
  // the stored finish when it is later; a stored finish that has fallen behind (not yet reconciled) never pulls the
  // card into the past. Pause / hold doctrine is untouched (non-bay holds still never extend the bay).
  if (optiFinishUtc && ticket.timer_status === 'running' && runLongMinutes > 0 && ticket.opti_finish) {
    const storedMs = new Date(ticket.opti_finish).getTime();
    if (Number.isFinite(storedMs) && storedMs > optiFinishUtc.getTime()) optiFinishUtc = new Date(storedMs);
  }

  // --- EOD risk / rollover gate ---
  let isAtEodRisk = false;
  let requiresRolloverGate = false;
  const eodBand = Math.max(
    0,
    typeof shopConfig?.eodRiskBandMin === 'number' ? shopConfig.eodRiskBandMin : 30,
  );
  // FLOYD-2 — ONE CLOCK.
  //
  // This compared `parseHmToMinutes(shift_end)` (minutes from SHOP-LOCAL
  // midnight) against minutes measured from UTC midnight, and called the
  // difference "a slight approximation". It is the shop's UTC offset: five
  // hours at Floyd's, always in the direction that makes work look later.
  //
  // Both sides are absolute instants now, so there is no origin to mismatch
  // and no calendar date to cross. The caller resolves the boundary through
  // the same dated-hours primitives `useAutoBump` uses, so the card DISPLAYS
  // the rollover authority's answer instead of computing a second one.
  const shiftEndUtc = input.shiftEndUtc ?? null;
  const shiftEndUtcMs =
    shiftEndUtc && Number.isFinite(shiftEndUtc.getTime()) ? shiftEndUtc.getTime() : null;
  if (
    shiftEndUtcMs !== null &&
    optiFinishUtc !== null &&
    input.startInstant !== null &&
    !isTerminalState(productionState)
  ) {
    const willingness = clampNonNeg(
      Number(shopConfig?.willingnessRollPastEodMin ?? 0),
    );
    // Shop culture, in the same units as the boundary it extends.
    const allowanceMs = shiftEndUtcMs + willingness * 60_000;
    const finishMs = optiFinishUtc.getTime();
    // Finishing AT the close is finishing today. The gate is for work that
    // runs past it.
    if (finishMs > allowanceMs) {
      requiresRolloverGate = true;
      isAtEodRisk = true;
    } else if (finishMs > allowanceMs - eodBand * 60_000) {
      isAtEodRisk = true;
    }
  }

  // --- Workload / occupancy ---
  // Q1 rules:
  //   - Unstarted + non-terminal → techOptiTime.
  //   - Active running → techOptiTime + runLong (bay-blocking).
  //   - Paused bay-blocking → techOptiTime + bayBlockingPauseMinutes + runLong.
  //   - Paused non-bay / held-in-parking → 0.
  //   - Terminal → 0.
  let workloadMinutes = 0;
  if (!isTerminalState(productionState)) {
    if (productionState === 'active_in_bay') {
      workloadMinutes = techOptiTime + occupancyRunLongMinutes;
    } else if (productionState === 'held_in_bay') {
      // Doctrinally bay-blocking unless break_failsafe suppressed.
      const bayMin =
        hold.channel === 'bay_blocking' ? hold.bayBlockingMinutes : 0;
      workloadMinutes = techOptiTime + bayMin + occupancyRunLongMinutes;
    } else if (
      productionState === 'held_in_parking' ||
      productionState === 'scheduled_but_parked' ||
      productionState === 'parking_lot_inactive'
    ) {
      // Non-bay → does not inflate tech/bay workload.
      workloadMinutes = 0;
    } else if (
      productionState === 'scheduled' ||
      productionState === 'estimate_prospect'
    ) {
      // Unstarted — show scheduled wall time.
      workloadMinutes = techOptiTime;
    } else {
      // Defensive fallback for any future state — treat as scheduled.
      workloadMinutes = techOptiTime;
    }
  }
  const scheduledOccupancyMinutes = workloadMinutes;

  // --- Display labels ---
  let holdChipLabel = '';
  if (hold.channel === 'bay_blocking' && hold.bayBlockingMinutes > 0) {
    holdChipLabel = 'Pause AutoBump';
  } else if (hold.channel === 'non_bay' && hold.nonBayMinutes > 0) {
    holdChipLabel = 'Hold time';
  } else if (hold.channel === 'break_failsafe_suppressed') {
    holdChipLabel = ''; // Suppressed by Bucket 5 default.
  }

  return {
    bookMinutes,
    predictedMinutes: bookMinutes,
    techOptiTime,
    elapsedMinutes: liveElapsedMinutes,
    liveElapsedMinutes,
    runLongMinutes,
    bayBlockingPauseMinutes: hold.bayBlockingMinutes,
    nonBayHoldMinutes: hold.nonBayMinutes,
    breakMinutes,
    pausedDuringBreakMinutes,
    scheduledOccupancyMinutes,
    workloadMinutes,
    optiFinishUtc,
    optiFinishMinutesFromStart,
    isAtEodRisk,
    requiresRolloverGate,
    holdChannel: hold.channel,
    productionState,
    displayLabels: {
      holdChipLabel,
      runLongChipLabel: 'Run Long',
    },
  };
}

// =============================================================================
// Convenience: workload reducer for the tech-column header.
// =============================================================================

/**
 * Sum `workloadMinutes` across a set of tickets for the tech-column
 * header. Returns the canonical occupancy minutes per Q1 rules:
 *   - Active running → techOptiTime + runLong (live projected)
 *   - Held-in-bay → techOptiTime + bay-blocking pause
 *   - Held-in-parking / non-bay → 0
 *   - Terminal → 0
 */
export function sumWorkloadMinutes(
  derived: ReadonlyArray<Pick<DerivedTicketTime, 'workloadMinutes'>>,
): number {
  let total = 0;
  for (const d of derived) {
    if (!d) continue;
    const w = Number(d.workloadMinutes ?? 0);
    if (Number.isFinite(w) && w > 0) total += w;
  }
  return total;
}
